import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { createCodeChallenge, randomUrlSafeString } from './pkce'

const TOKEN_KEY = 'kaldflyte_access_token'
const REFRESH_TOKEN_KEY = 'kaldflyte_refresh_token'
const VERIFIER_KEY = 'kaldflyte_pkce_verifier'
const STATE_KEY = 'kaldflyte_oidc_state'

interface OidcDiscovery {
  authorization_endpoint: string
  token_endpoint: string
  end_session_endpoint?: string
}

interface AuthContextValue {
  token: string | null
  authenticating: boolean
  error: string | null
  login: () => Promise<void>
  logout: () => void
  useBearerToken: (token: string) => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function defaultIssuer(): string {
  if (import.meta.env.VITE_OIDC_ISSUER_URL) {
    return import.meta.env.VITE_OIDC_ISSUER_URL.replace(/\/$/, '')
  }
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:8083/realms/kaldflyte'
  }
  return `${window.location.origin}/realms/kaldflyte`
}

const CLIENT_ID = import.meta.env.VITE_OIDC_CLIENT_ID || 'kaldflyte-dashboard'
const REDIRECT_URI =
  import.meta.env.VITE_OIDC_REDIRECT_URI || `${window.location.origin}/ui/`

async function discovery(): Promise<OidcDiscovery> {
  const response = await fetch(
    `${defaultIssuer()}/.well-known/openid-configuration`,
  )
  if (!response.ok) throw new Error('OIDC discovery failed')
  return response.json() as Promise<OidcDiscovery>
}

interface TokenResponse {
  access_token?: string
  refresh_token?: string
}

function storeTokens(payload: TokenResponse) {
  if (!payload.access_token) throw new Error('OIDC response missing access token')
  sessionStorage.setItem(TOKEN_KEY, payload.access_token)
  if (payload.refresh_token) {
    sessionStorage.setItem(REFRESH_TOKEN_KEY, payload.refresh_token)
  }
  return payload.access_token
}

function tokenExpiryMs(token: string): number | null {
  try {
    const payload = JSON.parse(
      atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as {exp?: number}
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null
  } catch {
    return null
  }
}

export function AuthProvider({ children }: PropsWithChildren) {
  const [token, setToken] = useState<string | null>(() =>
    sessionStorage.getItem(TOKEN_KEY),
  )
  const [authenticating, setAuthenticating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    if (!code) return

    const returnedState = params.get('state')
    const verifier = sessionStorage.getItem(VERIFIER_KEY)
    const expectedState = sessionStorage.getItem(STATE_KEY)
    if (!verifier || !returnedState || returnedState !== expectedState) {
      setError('OIDC callback state validation failed')
      return
    }

    setAuthenticating(true)
    void discovery()
      .then(async (configuration) => {
        const body = new URLSearchParams({
          grant_type: 'authorization_code',
          client_id: CLIENT_ID,
          code,
          redirect_uri: REDIRECT_URI,
          code_verifier: verifier,
        })
        const response = await fetch(configuration.token_endpoint, {
          method: 'POST',
          headers: {'Content-Type': 'application/x-www-form-urlencoded'},
          body,
        })
        if (!response.ok) throw new Error('OIDC token exchange failed')
        const payload = (await response.json()) as TokenResponse
        const accessToken = storeTokens(payload)
        sessionStorage.removeItem(VERIFIER_KEY)
        sessionStorage.removeItem(STATE_KEY)
        setToken(accessToken)
        window.history.replaceState({}, '', `${window.location.pathname}${window.location.hash}`)
      })
      .catch((reason: unknown) => {
        setError(reason instanceof Error ? reason.message : String(reason))
      })
      .finally(() => setAuthenticating(false))
  }, [])

  useEffect(() => {
    if (!token) return
    const refreshToken = sessionStorage.getItem(REFRESH_TOKEN_KEY)
    const expiresAt = tokenExpiryMs(token)
    if (!refreshToken || expiresAt === null) return

    const refresh = async () => {
      try {
        const configuration = await discovery()
        const body = new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: CLIENT_ID,
          refresh_token: sessionStorage.getItem(REFRESH_TOKEN_KEY) || '',
        })
        const response = await fetch(configuration.token_endpoint, {
          method: 'POST',
          headers: {'Content-Type': 'application/x-www-form-urlencoded'},
          body,
        })
        if (!response.ok) throw new Error('OIDC token refresh failed')
        setToken(storeTokens((await response.json()) as TokenResponse))
        setError(null)
      } catch (reason) {
        sessionStorage.removeItem(TOKEN_KEY)
        sessionStorage.removeItem(REFRESH_TOKEN_KEY)
        setToken(null)
        setError(reason instanceof Error ? reason.message : String(reason))
      }
    }

    // Renew one minute before access-token expiry. Each successful rotation
    // updates token state and schedules the next renewal.
    const delayMs = Math.max(0, expiresAt - Date.now() - 60_000)
    const timer = window.setTimeout(() => void refresh(), delayMs)
    return () => window.clearTimeout(timer)
  }, [token])

  const login = useCallback(async () => {
    setError(null)
    const configuration = await discovery()
    const verifier = randomUrlSafeString()
    const state = randomUrlSafeString(24)
    const challenge = await createCodeChallenge(verifier)
    sessionStorage.setItem(VERIFIER_KEY, verifier)
    sessionStorage.setItem(STATE_KEY, state)
    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      redirect_uri: REDIRECT_URI,
      response_type: 'code',
      scope: 'openid profile email',
      code_challenge: challenge,
      code_challenge_method: 'S256',
      state,
    })
    window.location.assign(`${configuration.authorization_endpoint}?${params}`)
  }, [])

  const logout = useCallback(() => {
    sessionStorage.removeItem(TOKEN_KEY)
    sessionStorage.removeItem(REFRESH_TOKEN_KEY)
    setToken(null)
  }, [])

  const useBearerToken = useCallback((value: string) => {
    const trimmed = value.trim().replace(/^Bearer\s+/i, '')
    if (!trimmed) return
    sessionStorage.setItem(TOKEN_KEY, trimmed)
    sessionStorage.removeItem(REFRESH_TOKEN_KEY)
    setToken(trimmed)
    setError(null)
  }, [])

  const value = useMemo(
    () => ({token, authenticating, error, login, logout, useBearerToken}),
    [authenticating, error, login, logout, token, useBearerToken],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider')
  return value
}
